import React, { useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import {
  AutoCompleteShipInfo,
  useAutocompleteDbShips,
} from '@ais-ui/websockets';
import {
  Badge,
  Button,
  Command,
  Input,
  LoadingSpinner,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { X } from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import { useInfiniteSearchShips } from '../Map/Search/AISShipSearch';
import { cn, Ship } from '@ais-ui/utils';
import { PiBoatThin, PiX } from 'react-icons/pi';
import { ShipSearchResult } from '../Map/Search/useShipSearch';
import { useLocalization } from '@ais-ui/locale-management';
import { IoMdInformationCircleOutline } from 'react-icons/io';

interface MultiShipCommandProps {
  value: AutoCompleteShipInfo[];
  onChange: (value: AutoCompleteShipInfo[]) => void;
}

export const MultiShipCommand = ({
  value = [], // Add default empty array
  onChange,
}: MultiShipCommandProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const commandRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);

  const [searchString, setSearchString] = useState('');
  const bouncedSearchString = useDebounce(searchString, 200);

  const { data: searchShips, isLoading } =
    useAutocompleteDbShips(bouncedSearchString);

  // Close dropdown when clicking outside the entire command
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        commandRef.current &&
        !commandRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // For some reason this autocomplete returns a different data structure than the one from ol server
  //  so we make the conversion here.
  const searchShipsInAutoCompleteFormat: AutoCompleteShipInfo[] =
    useMemo(() => {
      if (!searchShips) return [];
      return searchShips.map((ship) => ({
        name: ship.shipname,
        mmsi: ship.mmsi,
        imo: ship.imo,
        callSign: ship.callsign,
        type: '',
      }));
    }, [searchShips]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 32;
    const LOADING_SPINNER_HEIGHT = 68;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;

    let contentHeight = PADDING_AND_BORDERS;

    if (searchString.length < 3) {
      // Show "enter three or more characters" message
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else if (isLoading) {
      // Add loading spinner height
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (!searchShipsInAutoCompleteFormat.length) {
      // Add empty state height
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      // Add height for displayed ships
      contentHeight +=
        searchShipsInAutoCompleteFormat.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [searchShipsInAutoCompleteFormat.length, isLoading, searchString.length]);

  const handleUnselect = React.useCallback(
    (ship: AutoCompleteShipInfo) => {
      onChange(value.filter((s) => s.name !== ship.name));
    },
    [value, onChange]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (input.value === '') {
            onChange(value.slice(0, -1)); // Removes the last item from the array
          }
        }
        // This is not a default behaviour of the <input /> field
        if (e.key === 'Escape') {
          input.blur();
        }
      }
    },
    [value, onChange]
  );

  return (
    <Command
      ref={commandRef}
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div className="group border border-input px-3 py-2 text-sm rounded-md">
        <div className="flex gap-1 flex-wrap">
          {value.map((selectable) => {
            return (
              <Badge key={selectable.name} variant="secondary">
                {selectable.name}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnselect(selectable);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(selectable)}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            );
          })}
          <CommandPrimitive.Input
            ref={inputRef}
            value={searchString}
            onValueChange={setSearchString}
            onFocus={() => setOpen(true)}
            placeholder="search vessel..."
            className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
          />
        </div>
      </div>
      <div className="relative mt-2">
        {open && searchString.length < 3 && (
          <div
            className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
            style={{ height: `${calculatedHeight}px` }}
          >
            <div className="py-6 text-center text-sm">
              Enter three or more characters to search.
            </div>
          </div>
        )}

        {open && searchString.length >= 3 && (
          <div
            className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in p-0 overflow-hidden"
            style={{ height: `${calculatedHeight}px` }}
          >
            {isLoading ? (
              <div className="h-full flex justify-center items-center">
                <LoadingSpinner size="small" />
              </div>
            ) : searchShipsInAutoCompleteFormat &&
              searchShipsInAutoCompleteFormat.length > 0 ? (
              <ScrollArea className="h-full p-1">
                {searchShipsInAutoCompleteFormat.map((selectable) => {
                  const selectableName = `${selectable.name} (mmsi: ${
                    selectable.mmsi
                  }${selectable.imo ? `, imo: ${selectable.imo}` : ''})`;

                  // Check if this ship is already selected
                  const isAlreadySelected = value.some(
                    (selectedShip) => selectedShip.name === selectable.name
                  );

                  return (
                    <div
                      key={`${selectable.name}-${selectable.mmsi}-${selectable.imo}-${selectable.callSign}`}
                      className={`relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none ${
                        isAlreadySelected
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        // Only add if not already selected
                        if (!isAlreadySelected) {
                          setSearchString('');
                          onChange([...value, selectable]);
                        }
                      }}
                    >
                      {selectableName}
                      {isAlreadySelected && (
                        <span className="ml-auto text-xs">
                          Already selected
                        </span>
                      )}
                    </div>
                  );
                })}
              </ScrollArea>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No vessels found.
              </div>
            )}
          </div>
        )}
      </div>
    </Command>
  );
};

type ShipSelectorProps = {
  value: {
    name: string | null;
    mmsi: string | null;
    imo: string | null;
    callSign: string | null;
    type: string | null;
  } | null;
  onChange: (
    value: {
      name: string | null;
      mmsi: string | null;
      imo: string | null;
      callSign: string | null;
      type: string | null;
    } | null
  ) => void;
};

export const ShipSelector = ({ value, onChange }: ShipSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { messages } = useLocalization();

  // Using your existing hook (replace with your actual implementation)
  const { displayedShips, uniqueShips, viewportRef, isLoading } =
    useInfiniteSearchShips(25, searchString);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  };

  // Clear search and selection
  const clearSelection = () => {
    onChange(null);
    setSearchString('');
  };

  // Handle ship selection
  const onShipSelect = (ship: ShipSearchResult) => {
    const selectedShip = ship as AutoCompleteShipInfo;

    onChange({
      name: selectedShip?.name || '',
      mmsi: selectedShip?.mmsi?.toString() || null,
      imo: selectedShip?.imo?.toString() || null,
      callSign: selectedShip?.callSign || '',
      type: selectedShip?.type || '',
    });

    setSearchString('');
    setOpen(false);
  };

  return (
    <div className="relative w-full">
      <div
        className="w-full flex border rounded-lg justify-between items-center px-3 py-2 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 flex-grow text-sm">
          {value ? (
            <div className="flex items-center gap-2">
              <PiBoatThin className="h-4 w-4" />
              <span>{value.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">
              {messages.shipSearchText}
            </span>
          )}
        </div>

        <div className="flex space-x-2">
          {value && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
            >
              <PiX className="h-4 w-4" />
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <IoMdInformationCircleOutline className="text-primary" />
            </TooltipTrigger>
            <TooltipContent side="right">
              {messages.shipSearchTooltip}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Dropdown content */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover rounded-md border shadow-md">
          {/* Search input */}
          <div className="p-2 border-b">
            <Input
              ref={inputRef}
              autoFocus
              placeholder="Search for vessels..."
              value={searchString}
              onChange={handleSearchChange}
              className="w-full"
            />
          </div>

          {/* Results area */}
          <div className="pl-4 pt-4">
            <div className="py-1 px-2 text-xs font-medium text-muted-foreground">
              Vessels ({uniqueShips.length})
            </div>

            {!isLoading && searchString.length < 3 && (
              <div className="pt-2 pb-6 text-center text-sm">
                Enter three or more characters to search.
              </div>
            )}

            {!isLoading &&
              searchString.length >= 3 &&
              displayedShips.length === 0 && (
                <div className="py-6 text-center text-sm">
                  No vessels found.
                </div>
              )}

            {isLoading && (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="small" />
              </div>
            )}

            {displayedShips && displayedShips.length > 0 && (
              <ScrollArea className="h-[256px]" ref={viewportRef}>
                {displayedShips.map((ship, index) => (
                  <div
                    key={index}
                    className="flex flex-col space-y-1 p-2 hover:bg-accent rounded-lg cursor-pointer mr-4"
                    onClick={() => onShipSelect(ship)}
                  >
                    <div className="flex justify-between">
                      <div className="flex items-center space-x-1">
                        <PiBoatThin className="mr-2 h-4 w-4" />
                        {ship.name}
                      </div>
                    </div>
                    <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                      <div>MMSI: {ship.mmsi}</div>
                      <div
                        className={cn(
                          'rounded-full h-1 w-1',
                          ship.requestType === 'ais' && 'bg-blue-600',
                          ship.requestType === 'database' && 'bg-red-500'
                        )}
                      />
                      <div>IMO: {ship.imo ? ship.imo : 'None'}</div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
